// components/common/Logo.jsx
import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';

const YeldiLogo = ({ isDark, size = 32, showGlow = false, variant = "default" }) => {
  // Facteur d'échelle basé sur la taille
  const scale = size / 32;
  
  // Ajuster les positions pour les éléments internes
  const centerX = 16 * scale;
  const centerY = 16 * scale;
  const dotX = 9.5 * scale;
  const dotY = 5.8 * scale;
  const highlightX = 17.6 * scale;
  const highlightY = 14.2 * scale;

  // Ajustements pour la version neumorphique
  const isNeumorph = variant === "neumorph";

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} fill="none" aria-hidden="true">
      {/* Ombre portée pour l'effet neumorphique */}
      {isNeumorph && (
        <filter id="neumorph-shadow" x="-50%" y="-50%" width="200%" height="200%">
          <feDropShadow dx="2" dy="2" stdDeviation="3" floodOpacity="0.15" floodColor={isDark ? "#000000" : "#8b9bb5"}/>
          <feDropShadow dx="-2" dy="-2" stdDeviation="3" floodOpacity="isDark ? 0.2 : 0.4" floodColor={isDark ? "#2a3a5a" : "#ffffff"}/>
        </filter>
      )}
      
      {/* Anneau extérieur - blanc en dark */}
      <circle 
        cx={centerX} 
        cy={centerY} 
        r={13 * scale} 
        stroke={isDark ? "white" : isNeumorph ? "#cbd5e1" : "url(#logoRing)"} 
        strokeWidth={2.2 * scale} 
        fill="none" 
        strokeLinecap="round" 
        strokeDasharray={`${56 * scale} ${26 * scale}`}
        filter={isNeumorph ? "url(#neumorph-shadow)" : undefined}
        style={isDark && showGlow && !isNeumorph ? { filter: "drop-shadow(0 0 4px rgba(255,255,255,0.3))" } : {}}
      />
      
      {/* Cercle teal - gardé en dark aussi pour contraste */}
      <circle 
        cx={centerX} 
        cy={centerY} 
        r={7.5 * scale} 
        fill={isDark ? "#00c9a7" : isNeumorph ? "#00c9a7" : "url(#logoTeal)"} 
        opacity={isDark ? 1 : 0.95}
        filter={isNeumorph ? "url(#neumorph-shadow)" : undefined}
      />
      
      {/* Centre - bleu foncé */}
      <circle 
        cx={centerX} 
        cy={centerY} 
        r={3.6 * scale} 
        fill="#1b2a6b"
        filter={isNeumorph ? "url(#neumorph-shadow)" : undefined}
      />
      
      {/* Reflet - gardé blanc */}
      <circle 
        cx={highlightX} 
        cy={highlightY} 
        r={1.3 * scale} 
        fill="white" 
        opacity={isDark ? 1 : 0.9}
      />
      
      {/* Satellite dot - blanc en dark */}
      <circle 
        cx={dotX} 
        cy={dotY} 
        r={2.5 * scale} 
        fill={isDark ? "white" : isNeumorph ? "#cbd5e1" : "url(#logoDot)"}
        filter={isNeumorph ? "url(#neumorph-shadow)" : undefined}
        style={isDark && showGlow && !isNeumorph ? { filter: "drop-shadow(0 0 3px rgba(255,255,255,0.4))" } : {}}
      />
      
      {/* Dégradés uniquement définis en mode light */}
      {!isDark && !isNeumorph && (
        <defs>
          <linearGradient id="logoRing" x1={3 * scale} y1={3 * scale} x2={29 * scale} y2={29 * scale} gradientUnits="userSpaceOnUse">
            <stop offset="0%" stopColor="#1b2a6b"/>
            <stop offset="100%" stopColor="#2d3f8f"/>
          </linearGradient>
          <linearGradient id="logoTeal" x1={8.5 * scale} y1={8.5 * scale} x2={23.5 * scale} y2={23.5 * scale} gradientUnits="userSpaceOnUse">
            <stop offset="0%" stopColor="#00e5c0"/>
            <stop offset="100%" stopColor="#00c9a7"/>
          </linearGradient>
          <linearGradient id="logoDot" x1={7 * scale} y1={3.3 * scale} x2={12 * scale} y2={8.3 * scale} gradientUnits="userSpaceOnUse">
            <stop offset="0%" stopColor="#1b2a6b"/>
            <stop offset="100%" stopColor="#2d3f8f"/>
          </linearGradient>
        </defs>
      )}
    </svg>
  );
};

const Logo = ({ size = 32, showGlow = true, variant = "default", className = "" }) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  // Version neumorphique (comme l'avatar du profile)
  if (variant === "neumorph") {
    return (
      <div 
        className={className} 
        style={{
          width: size, 
          height: size,
          borderRadius: '50%',
          background: isDark 
            ? 'rgba(255,255,255,.18)' 
            : '#f0f3f8',
          border: isDark 
            ? '2px solid rgba(255,255,255,.35)' 
            : '2px solid rgba(255,255,255,.8)',
          boxShadow: isDark
            ? 'none'
            : '8px 8px 16px #d1d9e6, -8px -8px 16px #ffffff',
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          transition: 'all 0.2s ease',
        }}
      >
        <YeldiLogo 
          isDark={isDark} 
          size={size * 0.7} 
          showGlow={showGlow}
          variant="neumorph"
        />
      </div>
    );
  }

  // Version minimal avec fond (pour login)
  if (variant === "minimal") {
    return (
      <div className={className} style={{
        width: size, 
        height: size,
        background: isDark 
          ? 'rgba(0, 201, 167, 0.15)' 
          : 'rgb(255, 255, 255)',
        borderRadius: size * 0.28,
        border: isDark 
          ? '1px solid rgba(255, 255, 255, 0.2)' 
          : '1px solid rgba(255, 255, 255, 0.5)',
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        backdropFilter: 'blur(8px)',
        boxShadow: isDark
          ? 'none'
          : '4px 4px 10px rgba(163, 177, 198, 0.3), -4px -4px 10px rgba(255, 255, 255, 0.8)',
      }}>
        <YeldiLogo isDark={isDark} size={size * 0.7} showGlow={showGlow} />
      </div>
    );
  }

  // Version standard (sans fond)
  return <YeldiLogo isDark={isDark} size={size} showGlow={showGlow} />;
};

export default Logo;